import * as React from 'react'
import { Layout, Responsive, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

import { Box, Card, Grid, Icon, LargeIcon, Tag, Text } from '@factorialco/deprecated-design-system'
import { Player } from '@lottiefiles/react-lottie-player'
import classnames from 'classnames/bind'
import deepEqual from 'deep-equal'
import { Features } from 'generated/featureDefinitions'
import { isNil } from 'lodash'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'

import { observer } from 'mobx-react'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import ChartItem from 'modules/reports/components/DashboardShow/components/Content/Chart/Item'
import DashboardFilters from 'modules/reports/components/DashboardShow/components/Content/DashboardFilters'
import { VariableValueForm } from 'modules/reports/components/DashboardShow/components/Content/DashboardFilters/variableValueForm'
import animation from 'modules/reports/components/DashboardShow/components/Content/chart.json'
import styles from 'modules/reports/components/DashboardShow/components/Content/index.module.css'
import { reportSizes } from 'modules/reports/components/DashboardShow/components/Content/reportSizes'
import { featureToRequest } from 'modules/reports/components/DashboardShow/components/upsellUtils'
import { ShareReportsPolicy } from 'modules/reports/components/DashboardShow/types'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { useUserETL } from 'modules/reports/hooks/useUserETL'
import type DashboardModel from 'modules/reports/store/models/Dashboard'
import type { Variable } from 'modules/reports/store/models/Report'
import { UpsellingKit } from 'modules/upselling_kit/components'

const ResponsiveReactGridLayout = WidthProvider(Responsive)
const cx = classnames.bind(styles)

const DASHBOARD_COLS = 4

const generateLayout = (dashboard: DashboardModel) => {
  const items = dashboard.toJS().items
  const { reports } = dashboard

  if (!items) {
    return {}
  }

  // We ensure each report type has its minimal sizes
  const itemsWithMins = items.map((item: Layout) => {
    const report = reports.find((report) => report.id === item.i)
    const itemSizes = reportSizes(report?.attributes.chart_type)

    const minW = itemSizes.minW
    const minH = itemSizes.minH

    // Setting minH and minW
    let w = item.w < minW ? minW : item.w
    let h = item.h < minH ? minH : item.h

    // New report, set defaults
    if (isNil(item.minW) || isNil(item.minH)) {
      w = itemSizes.initialW
      h = itemSizes.initialH
    }

    return { ...item, w, h, minW, minH }
  })

  return {
    lg: itemsWithMins,
    md: itemsWithMins,
    sm: itemsWithMins,
    xs: itemsWithMins,
    xxs: itemsWithMins,
  }
}

const dashboardHasChanges = (dashboard: DashboardModel, layout: Layout[]): boolean => {
  const items = dashboard.toJS().items

  if (!items) {
    return true
  }

  const cleanedItems = items.map((item: Layout) => omitBy(item, isUndefined))
  const cleanedLayout = layout.map((item) => omitBy(item, isUndefined))

  return !deepEqual(cleanedItems, cleanedLayout)
}

interface ItemCardProps {
  to: string
  icon: LargeIcon
  title: string
  description: string
  showUpsellTag?: boolean
}

const ItemCard = ({ to, icon, title, description, showUpsellTag }: ItemCardProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const requestProps = featureToRequest()

  const onClick = () => {
    if (showUpsellTag) setIsModalOpen(true)
  }

  return (
    <>
      <Card to={showUpsellTag ? undefined : to} onClick={onClick}>
        <Card.Eyebrow>
          <Icon.Large icon={icon} color="grey800" />
          {showUpsellTag ? (
            <Tag type="grey3" leftItem="icon" icon="lock-locked">
              {i18n.t('custom_reports.dashboards.upsell_modal.upgrade_button')}
            </Tag>
          ) : undefined}
        </Card.Eyebrow>
        <Card.Title>{title}</Card.Title>
        <Card.Description>{description}</Card.Description>
      </Card>
      {isModalOpen && requestProps.moduleRequested && (
        <UpsellingKit.FeatureModal
          moduleRequested={requestProps.moduleRequested}
          requestSource={requestProps.requestSource ?? 'Upsell modal with feature info'}
          requestReason={requestProps.requestReason}
          featureRequested={Features.analytics_advanced_reports}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}

const BlankSlate = ({ dashboard }: { dashboard: DashboardModel }) => {
  const { navigation } = useNavigation()

  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  const showUpsellTag = !featureAvailability.available

  return (
    <Box alignItems="center">
      <Box width="narrowPage" justifyContent="center">
        <Grid variant="large">
          <Box>
            <ItemCard
              to={navigation.reports.dashboards.dashboardId.templates.fullPath({
                dashboard_id: dashboard.id,
              })}
              icon="analytics-bars"
              title={i18n.t('custom_reports.dashboards.blank_slate.create_widget.library.title')}
              description={i18n.t(
                'custom_reports.dashboards.blank_slate.create_widget.library.description'
              )}
            />
          </Box>
          <Box>
            <ItemCard
              to={navigation.reports.dashboards.dashboardId.new.fullPath({
                dashboard_id: dashboard.id,
              })}
              showUpsellTag={showUpsellTag}
              icon="workflow"
              title={i18n.t('custom_reports.dashboards.blank_slate.create_widget.builder.title')}
              description={i18n.t(
                'custom_reports.dashboards.blank_slate.create_widget.builder.description'
              )}
            />
          </Box>
          <Box>
            <ItemCard
              to={navigation.reports.dashboards.dashboardId.sql.fullPath({
                dashboard_id: dashboard.id,
              })}
              showUpsellTag={showUpsellTag}
              icon="window-code"
              title={i18n.t('custom_reports.dashboards.blank_slate.create_widget.sql_editor.title')}
              description={i18n.t(
                'custom_reports.dashboards.blank_slate.create_widget.sql_editor.description'
              )}
            />
          </Box>
          <Box>
            <ItemCard
              to={navigation.reports.dashboards.dashboardId.list.question.fullPath({
                dashboard_id: dashboard.id,
              })}
              showUpsellTag={showUpsellTag}
              icon="events-communications"
              title={i18n.t('custom_reports.dashboards.blank_slate.create_widget.ai.title')}
              description={i18n.t(
                'custom_reports.dashboards.blank_slate.create_widget.ai.description'
              )}
            />
          </Box>
        </Grid>
      </Box>
    </Box>
  )
}

interface Props {
  dashboard: DashboardModel
  filtersValues: VariableValueForm[]
  showAddFilterModal: boolean
  setShowAddFilterModal: (value: boolean) => () => void
  reportsSharePolicy: ShareReportsPolicy[]
  canEdit: boolean
}

const Content = ({
  dashboard,
  filtersValues,
  showAddFilterModal,
  setShowAddFilterModal,
  reportsSharePolicy,
  canEdit,
}: Props) => {
  const { track } = useReportTracking()

  const { isEmpty, lastRefreshTime } = useUserETL({
    readOnly: false,
  })

  React.useEffect(() => {
    track(trackingEvents.reports.reportsDashboardVisited, {
      id: dashboard.attributes.id,
    })
    track(trackingEvents.reports.openReportsDashboard, {
      dashboard_id: dashboard.attributes.id,
    })
  }, [track, dashboard])

  const reports = dashboard?.reports || []

  // React useEffect don't evaluate objects well, so we need to hack it
  const intermediateValue = filtersValues.map((form) => form.data())
  const checksum = JSON.stringify(intermediateValue)

  const variables = React.useMemo(
    () => filtersValues.map((form) => form.data() as Variable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checksum]
  )

  if (!dashboard) return null

  const onLayoutChange = async (layout: Layout[]) => {
    if (canEdit && dashboardHasChanges(dashboard, layout)) {
      await asyncAction(
        () =>
          dashboard.save(
            {
              items: layout,
            },
            {}
          ),
        {
          successFlash: false,
        }
      )
    }
  }

  if (isEmpty)
    return (
      <Box justifyContent="center" alignItems="center" minHeight="full-view-height">
        <Box>
          <Player autoplay loop src={animation} style={{ height: '120px' }}></Player>
        </Box>
        <Box justifyContent="center" paddingBottom="s8">
          <Text.Header2>{i18n.t('custom_reports.dashboard.refresh.title')}</Text.Header2>
        </Box>
        <Box textAlign="center" paddingX="s200" justifyContent="center">
          <Text size="300" color="grey800">
            {i18n.t('custom_reports.dashboard.refresh.description')}
          </Text>
        </Box>
      </Box>
    )

  return (
    <>
      <Box gap="s40" flexGrow width="full">
        {reports.length === 0 && <BlankSlate dashboard={dashboard} />}
        {reports.length > 0 && (
          <Box gap="s32">
            <DashboardFilters
              dashboard={dashboard}
              filterForms={filtersValues}
              showAddFilterModal={showAddFilterModal}
              canEdit={canEdit}
              setShowAddFilterModal={setShowAddFilterModal}
            />
            <ResponsiveReactGridLayout
              className={styles.chartList}
              draggableCancel="button"
              onLayoutChange={onLayoutChange}
              cols={{
                lg: DASHBOARD_COLS,
                md: DASHBOARD_COLS,
                sm: DASHBOARD_COLS,
                xs: DASHBOARD_COLS,
                xxs: DASHBOARD_COLS,
              }}
              rowHeight={215}
              margin={[30, 30]}
              containerPadding={[0, 0]}
              layouts={generateLayout(dashboard) as any}
            >
              {reports.map((report) => (
                <div key={report.id} className={cx('cell')}>
                  <ChartItem
                    report={report}
                    variables={variables}
                    canEdit={canEdit}
                    lastRefreshTimeString={lastRefreshTime?.toISOString()}
                    reportsSharePolicy={reportsSharePolicy}
                  />
                </div>
              ))}
            </ResponsiveReactGridLayout>
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(Content)
